import {HStack, Show, Text} from 'platform/foundation';

import {Fragment, ReactNode} from 'react';

import {EntityResourceIds} from '@dms/api/shared';

import {buildArray, DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {AssigneeV2} from '../../Assignment/AssigneeV2';
import {ParkingPlace} from '../../ParkingPlace/ParkingPlace';
import {Source} from '../../Source/Source';
import {HeaderPermissions} from '../types/headerPermissions';
import {AuthorizationProfile} from './AuthorizationProfile';
import {BusinessCaseBranch} from './BusinessCaseBranch';
import {BusinessCaseCustomId} from './BusinessCaseCustomId';
import {InterestBranch} from './InterestBranch';
import {KeyLocation} from './KeyLocation';
import {Location} from './Location';
import {RentalBranch} from './RentalBranch';
import {RentalVehicle} from './RentalVehicle';
import {SalePreset} from './SalePreset';
import {ServiceBranch} from './ServiceBranch';
import {TireBranch} from './TireBranch';
import {VehicleCustomId} from './VehicleCustomId';
import {VehicleLocation} from './VehicleLocation';
import {VehicleWarehouse} from './VehicleWarehouse';
import {WorkshopInterestBranch} from './WorkshopInterestBranch';
import {WorkshopInterestSource} from './WorkshopInterestSource';

export type EntityHeaderControl =
  | 'LOCATION'
  | 'VEHICLE_LOCATION'
  | 'SERVICE_BRANCH'
  | 'VEHICLE_WAREHOUSE'
  | 'SALE_PRESET'
  | 'PARKING'
  | 'KEY'
  | 'ASSIGNEE'
  | 'BC_CUSTOM_ID'
  | 'VH_CUSTOM_ID'
  | 'BC_BRANCH'
  | 'INTEREST_BRANCH'
  | 'RENTAL_BRANCH'
  | 'RENTAL_VEHICLE'
  | 'SOURCE'
  | 'TIRE_BRANCH'
  | 'AUTHORIZATION_PROFILE'
  | 'WORKSHOP_INTEREST_BRANCH'
  | 'WORKSHOP_INTEREST_SOURCE';

export interface EntityHeaderControlsProps extends TestIdProps {
  controls: EntityHeaderControl[];
  recordId: string;
  resourceId?: EntityResourceIds;
  parkingRecordId?: string;
  authorizationProfileRecordId?: string;
  isAuthorizationProfileEditable?: boolean;
  onAuthorizationProfileChange?: () => void;
  permissions: HeaderPermissions | undefined;
  enableTeams?: boolean;
}

export function EntityHeaderControls(props: EntityHeaderControlsProps) {
  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  if (!props.controls.length) {
    return null;
  }

  const isSalesModuleEnabled = canReadBusinessCase;

  const controlsComponent = buildArray<{component: ReactNode; key: string}>()
    .when(props.controls.includes('RENTAL_VEHICLE'), {
      key: 'RENTAL_VEHICLE',
      component: (
        <RentalVehicle recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('BC_BRANCH'), {
      key: 'BC_BRANCH',
      component: (
        <BusinessCaseBranch
          recordId={props.recordId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('RENTAL_BRANCH'), {
      key: 'RENTAL_BRANCH',
      component: (
        <RentalBranch recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('INTEREST_BRANCH'), {
      key: 'INTEREST_BRANCH',
      component: (
        <InterestBranch recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('SOURCE'), {
      key: 'SOURCE',
      component: (
        <Source
          recordId={props.recordId}
          resourceId={props.resourceId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('LOCATION'), {
      key: 'LOCATION',
      component: (
        <Location recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('VEHICLE_LOCATION'), {
      key: 'VEHICLE_LOCATION',
      component: (
        <VehicleLocation recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('TIRE_BRANCH'), {
      key: 'TIRE_BRANCH',
      component: (
        <TireBranch recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('SERVICE_BRANCH'), {
      key: 'SERVICE_BRANCH',
      component: (
        <ServiceBranch recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('VEHICLE_WAREHOUSE') && isSalesModuleEnabled, {
      key: 'VEHICLE_WAREHOUSE',
      component: (
        <VehicleWarehouse vehicleId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('SALE_PRESET'), {
      key: 'SALE_PRESET',
      component: (
        <SalePreset recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('PARKING'), {
      key: 'PARKING',
      component: (
        <ParkingPlace
          recordId={props.parkingRecordId ?? props.recordId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('AUTHORIZATION_PROFILE'), {
      key: 'AUTHORIZATION_PROFILE',
      component: (
        <AuthorizationProfile
          recordId={props.authorizationProfileRecordId ?? ''}
          isEditable={props.isAuthorizationProfileEditable}
          onChange={props.onAuthorizationProfileChange}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('KEY'), {
      key: 'KEY',
      component: (
        <KeyLocation recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    })
    .when(props.controls.includes('WORKSHOP_INTEREST_BRANCH'), {
      key: 'WORKSHOP_INTEREST_BRANCH',
      component: (
        <WorkshopInterestBranch
          recordId={props.recordId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('WORKSHOP_INTEREST_SOURCE'), {
      key: 'WORKSHOP_INTEREST_SOURCE',
      component: (
        <WorkshopInterestSource
          recordId={props.recordId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('ASSIGNEE') && props.resourceId, {
      key: 'ASSIGNEE',
      component: (
        <AssigneeV2
          recordId={props.recordId}
          resourceId={props.resourceId!}
          enableTeams={props.enableTeams}
          data-testid={suffixTestId('control', props)}
          participationPermissions={props.permissions?.participation}
        />
      ),
    })
    .when(props.controls.includes('BC_CUSTOM_ID'), {
      key: 'BC_CUSTOM_ID',
      component: (
        <BusinessCaseCustomId
          recordId={props.recordId}
          data-testid={suffixTestId('control', props)}
        />
      ),
    })
    .when(props.controls.includes('VH_CUSTOM_ID'), {
      key: 'VH_CUSTOM_ID',
      component: (
        <VehicleCustomId recordId={props.recordId} data-testid={suffixTestId('control', props)} />
      ),
    });

  return (
    <HStack spacing={1} align="center" wrap>
      {controlsComponent.map(({key, component}, index) => (
        <Fragment key={key}>
          <Show when={index !== 0}>
            <Text color="tertiary" size="small">
              {DOT_CHARACTER}
            </Text>
          </Show>
          {component}
        </Fragment>
      ))}
    </HStack>
  );
}
